import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { useEffect } from 'react';
import { Container, Form, InputGroup } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type { PurchaseDonation } from '@furality/fox-legacy-client/models';
import { AuthState, ReportError, useAuth, useQuery } from '@furality/frontend-stack';
import { Button, Slice } from '@furality/furality-purpose';
import registration from '#/api/registration.ts';
import DonationsReceived from '@/components/_donate/DonationsReceived.tsx';
import DonationInput, { getDonationInputValidation } from '@/components/DonationInput.tsx';
import LinkButton from '@/components/LinkButton.tsx';
import { RegisterLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import useFlag from '@/lib/hooks/useFlag.ts';
import useProfileQuery from '@/lib/queries/useProfileQuery.ts';
import useCart from '@/lib/useCart.tsx';

type DonationData = Pick<PurchaseDonation, 'amount' | 'donationMessage'>;

const DONATION_MESSAGE_MAX_LENGTH = 1000;

// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
const Donate = () => {
  const {
    t,
    i18n: { exists: i18nKeyExists },
  } = useTranslation(['common', 'donate']);

  const ourCharityURL = 'https://furality.org/charity';

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
    control,
  } = useForm<DonationData>();

  const profile = useProfileQuery(false);
  const navigate = useNavigate();
  const { products, addItem, cart, updateItem } = useCart();
  const donationItem = cart.data?.items.find((item) => item.type === 'donation');
  const donationProduct = products.data?.find((product) => product.type === 'donation');

  const donationAmount = useWatch({
    control,
    name: 'amount',
  });
  const donationMessage = useWatch({
    control,
    name: 'donationMessage',
  });
  const proceedToPaymentButtonDisabled =
    !donationAmount || !!errors.amount || !!errors.donationMessage;

  const handleSubmit = async () => {
    if (donationItem) {
      await updateItem.mutateAsync({
        lineId: donationItem.id,
        price: donationAmount * 100,
      });
    } else if (donationProduct) {
      await addItem.mutateAsync({
        productId: donationProduct.productId,
        price: donationAmount * 100,
      });
    }
    void navigate({ to: '/checkout' });
  };

  const featuresReady = useFlagsStatus().flagsReady;
  const enableDonations = useFlag('donations');
  const enableDonationMessages = useFlag('donations.messages');
  const enableCharity = useFlag('charity');
  const enableProfile = useFlag('profile');

  const auth = useAuth();

  useEffect(() => {
    if (auth.state === AuthState.NotLoggedIn) {
      auth.login();
    }
  }, [auth]);

  const charityTotal = useQuery({
    queryKey: ['registration', 'charity'],
    queryFn: async () => {
      const res = await registration.value.paymentGetCharity({ params: { format: '' } });
      switch (res.status) {
        case 200:
          // TODO: Remove the 'as' once the type is fixed in the api
          return res.data;
        default:
          throw new ReportError(res);
      }
    },
  });

  // We could just display it as is, but I like the funny count up animation, so we reparse it
  const parsedCharityTotal = parseFloat(
    charityTotal.data?.total.toString().replace(/[^0-9.-]+/g, '') ?? '0',
  );

  if (!featuresReady) {
    return <PageLoading />;
  }

  if (!enableDonations) {
    return (
      <Slice size="lg">
        <h3 className="mt-4">{t('donate:has_closed')}</h3>
        <LinkButton to="/profile" className="mt-4">
          {t('shared.return_to_profile')}
        </LinkButton>
      </Slice>
    );
  }

  if (profile.isLoading || profile.data === undefined) {
    return <PageLoading />;
  }

  return (
    <div className="mt-4 mt-xl-6 mb-md-4">
      <Container className="z-101 position-relative">
        <div className="mb-5 text-center">
          {enableCharity && (
            <>
              <h1>
                <Trans
                  // CON-SPECIFIC: charity name
                  i18nKey="shared.donation.title.f9"
                  components={{
                    charityLink: <a href={ourCharityURL} target="_blank" rel="noreferrer" />,
                  }}
                />
              </h1>
              {i18nKeyExists('blurb.f9', {
                ns: 'donate',
              }) && (
                <p className="mt-3" style={{ whiteSpace: 'pre-wrap' }}>
                  <Trans
                    ns="donate"
                    // CON-SPECIFIC: charity name
                    i18nKey="blurb.f9"
                    components={{
                      charityLink: <a href={ourCharityURL} target="_blank" rel="noreferrer" />,
                    }}
                  />
                </p>
              )}
              <DonationsReceived total={parsedCharityTotal} />
            </>
          )}
          <h3 id="active-step" className="mb-4">
            {t(
              // CON-SPECIFIC: charity name
              'shared.donate.how_much.f9',
            )}
          </h3>
          <Form className="fluid-paragraph" onSubmit={onSubmit(handleSubmit)}>
            <DonationInput
              className="mb-0"
              registerReturn={register('amount', getDonationInputValidation(t))}
              error={errors.amount}
            />

            {enableDonationMessages && (
              <>
                <InputGroup className="mt-2">
                  <Form.Control
                    type="text"
                    as="textarea"
                    aria-label={t('message')}
                    isInvalid={!!errors.donationMessage}
                    placeholder={t('donate:message_optional')}
                    {...register('donationMessage', {
                      maxLength: DONATION_MESSAGE_MAX_LENGTH,
                    })}
                    aria-describedby="donationMessageCharacters"
                  />
                </InputGroup>
                <Form.Text as="div" className="text-right" id="donationMessageCharacters" muted>
                  {donationMessage?.length ?? 0}/{DONATION_MESSAGE_MAX_LENGTH}
                </Form.Text>
              </>
            )}

            <Button
              className="mt-4"
              type="submit"
              variant="primary"
              disabled={proceedToPaymentButtonDisabled}
            >
              {t('donate:proceed_to_payment')}
            </Button>
          </Form>
        </div>
      </Container>
      {enableProfile && (
        <div className="delimiter-top">
          <LinkButton to="/profile" variant="outline-white" className="mt-5 center">
            {t('shared.cancel')}
          </LinkButton>
        </div>
      )}
    </div>
  );
};

export const Route = createFileRoute('/donate')({
  component: () => (
    <RegisterLayout title="shared.navigation.donate" noLogin>
      <Donate />
    </RegisterLayout>
  ),
});
